import type { Survey, SurveyOverallScores } from '@engagement-web-app/common';
import { createFileRoute, type ErrorComponentProps, Link, stripSearchParams } from '@tanstack/react-router';
import { BarChart3Icon, Sparkles, Star, Trophy } from 'lucide-react';
import { useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Skeleton } from '@/components/Skeleton';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import {
    DiAverageChartPerAttributes,
    DiAverageChartPerAttributesSkeleton,
} from '@/components/surveys/summaries/DiAverageChartPerAttributes';
import {
    DiAverageChartPerYear,
    DiAverageChartPerYearSkeleton,
} from '@/components/surveys/summaries/DiAverageChartPerYear';
import {
    DynamicAnalysisChart,
    DynamicAnalysisChartSkeleton,
} from '@/components/surveys/summaries/DynamicAnalysisChart';
import { GlobalScore, GlobalScoreSkeleton } from '@/components/surveys/summaries/GlobalScore';
import { ImprovementArea, ImprovementAreaSkeleton } from '@/components/surveys/summaries/ImprovementArea';
import { ResponderCount, ResponderCountSkeleton } from '@/components/surveys/summaries/ResponderCount';
import { TopCategory, TopCategorySkeleton } from '@/components/surveys/summaries/TopCategory';
import { fetchSurveys } from '@/queries/surveys/system';
import { hiddenInPrint, pageBreakBefore, pageBreakBeforeInLandscape } from '@/styles/print.css';
import { combineQueryErrors } from '@/utils';
import {
    useOverallScores,
    useScoresByAttributes,
    useScoresByCategory,
    useScoresByDepartment,
    useScoresByQuestion,
    useSurveyAttributes,
} from '../-hooks';
import { DefaultRouteQueryParams, validateSearch } from '../-search';
import { RankingsByDepartment, RankingsByDepartmentSkeleton } from './-fragments/RankingsByDepartment';

const ERROR_MESSAGE = '全社サマリーの取得に失敗しました。'; // also used below for the in-page query error

export const Route = createFileRoute('/system/_auth/surveys/summaries/')({
    loaderDeps({ search: { organizationId, surveyId } }) {
        return { organizationId, surveyId };
    },
    loader: async ({ deps }) => {
        return fetchSurveys(deps.organizationId);
    },
    validateSearch,
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    component: SurveySummariesRoute,
    pendingComponent: ContentSkeleton,
    errorComponent: SurveySummariesTabError,
});

function SurveySummariesTabError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} message={ERROR_MESSAGE} />;
}

export function SurveySummariesRoute() {
    const { organizationId, surveyId: selectedSurveyId } = Route.useSearch();
    const { surveys } = Route.useLoaderData();

    const selectedSurveyIdx = surveys.findIndex((s) => s.surveyId === selectedSurveyId);
    const surveyIdsToLoad = surveys.slice(selectedSurveyIdx).map((s) => s.surveyId);
    const overallScoresQuery = useOverallScores(organizationId, surveyIdsToLoad);

    const selectedSurvey = surveys.find((s) => s.surveyId === selectedSurveyId);

    return (
        <>
            {overallScoresQuery.isPending && <ContentSkeleton />}

            <ErrorOverlay
                active={overallScoresQuery.isError}
                onRetry={overallScoresQuery.refetch}
                message={ERROR_MESSAGE}
            />

            {overallScoresQuery.data && selectedSurvey && (
                <Content
                    organizationId={organizationId}
                    scores={overallScoresQuery.data.scores}
                    selectedSurvey={selectedSurvey}
                    isError={overallScoresQuery.isError}
                    onRetry={overallScoresQuery.refetch}
                />
            )}
        </>
    );
}

type SurveySummariesContentProps = {
    organizationId: string;
    /**
     * Overall scores for each of the organization's surveys.
     * - Must contain all surveys.
     * - Must be sorted from newest to oldest.
     */
    scores: SurveyOverallScores[];
    selectedSurvey: Survey;
    /**
     * `Content` only renders once `overallScoresQuery.data` exists, but that doesn't
     * make this permanently `false` — on a failed background refetch (window refocus,
     * reconnect, a stale remount), React Query flips `isError` to `true` while leaving
     * `data` at its last successful value, so this can still fire after the initial load.
     */
    isError: boolean;
    onRetry: () => void;
};

function Content({ organizationId, scores, selectedSurvey, isError, onRetry }: SurveySummariesContentProps) {
    const [selectedSettingId, setSelectedSettingId] = useState<string | null>(null);

    const selectedSurveyId = selectedSurvey.surveyId;
    const previousSurveyId = selectedSurvey.previousSurveyId;

    const currentSurveyScores = scores.find((s) => s.surveyId === selectedSurveyId)!;
    const previousSurveyScores = previousSurveyId ? scores.find((s) => s.surveyId === previousSurveyId) : undefined;
    const surveyIdsToCompare = [selectedSurveyId, previousSurveyId].filter((id): id is string => Boolean(id));

    const surveyAttributesQuery = useSurveyAttributes(organizationId, selectedSurveyId);

    const scoresByAttributeQuery = useScoresByAttributes(
        organizationId,
        selectedSurveyId,
        selectedSettingId,
        previousSurveyId
    );
    const scoresByAttributeError = combineQueryErrors(surveyAttributesQuery, scoresByAttributeQuery);
    const currentScoresByAttribute = scoresByAttributeQuery.data?.surveys.find((s) => s.surveyId === selectedSurveyId);
    const previousScoresByAttribute = scoresByAttributeQuery.data?.surveys.find((s) => s.surveyId === previousSurveyId);

    const scoresByCategoryQuery = useScoresByCategory(organizationId, surveyIdsToCompare);
    const currentSurveyScoresByCategory = scoresByCategoryQuery.data?.scores.find(
        (s) => s.surveyId === selectedSurveyId
    );
    const previousSurveyScoresByCategory = scoresByCategoryQuery.data?.scores.find(
        (s) => s.surveyId === previousSurveyId
    );

    const scoresByQuestionQuery = useScoresByQuestion(organizationId, [selectedSurveyId]);
    const currentSurveyScoresByQuestions = scoresByQuestionQuery.data?.scores.at(0);

    const improvementAreaError = combineQueryErrors(scoresByCategoryQuery, scoresByQuestionQuery);

    const {
        scoresByDepartment,
        isError: isScoresByDepartmentError,
        retry: retryScoresByDepartment,
    } = useScoresByDepartment(organizationId, selectedSurveyId);
    const currentSurveyScoresByDepartment = scoresByDepartment?.data?.surveys.at(0)?.scores ?? null;

    const { highestCategory, mostImprovedCategory } = currentSurveyScores;

    return (
        <Flex direction="column" gap="5">
            <Flex justify="end" className={hiddenInPrint}>
                <CreatePdfButton />
            </Flex>

            <Flex gap="4">
                <GlobalScore
                    icon={Star}
                    title="DI 平均"
                    scores={{ value: currentSurveyScores.di, previousValue: previousSurveyScores?.di ?? null }}
                    isError={isError}
                    onRetry={onRetry}
                />
                <GlobalScore
                    icon={BarChart3Icon}
                    title="偏差値"
                    scores={{
                        value: currentSurveyScores.deviation,
                        previousValue: previousSurveyScores?.deviation ?? null,
                    }}
                    isError={isError}
                    onRetry={onRetry}
                />
                <ResponderCount
                    responders={currentSurveyScores.responseRate.responders}
                    isError={isError}
                    onRetry={onRetry}
                />
            </Flex>

            {(highestCategory || mostImprovedCategory) && (
                <Flex gap="4">
                    {highestCategory && (
                        <TopCategory
                            icon={Trophy}
                            title="最高評価カテゴリー"
                            category={highestCategory}
                            isError={isError}
                            onRetry={onRetry}
                        />
                    )}
                    {mostImprovedCategory && (
                        <TopCategory
                            icon={Sparkles}
                            title="一番改善できたカテゴリー"
                            category={mostImprovedCategory}
                            isError={isError}
                            onRetry={onRetry}
                        />
                    )}
                </Flex>
            )}

            <div className={pageBreakBeforeInLandscape}>
                <RankingsByDepartment
                    organizationId={organizationId}
                    attributes={surveyAttributesQuery.data?.attributes ?? null}
                    scores={currentSurveyScoresByDepartment}
                    isError={isScoresByDepartmentError}
                    onRetry={retryScoresByDepartment}
                />
            </div>

            {/* Print layout: scores, rankings, the charts, then the analyses. */}
            <Flex gap="4" className={pageBreakBefore}>
                <DiAverageChartPerYear scores={scores} isError={isError} onRetry={onRetry} />

                <DiAverageChartPerAttributes
                    scores={currentScoresByAttribute?.scores ?? null}
                    previousScores={previousScoresByAttribute?.scores ?? null}
                    attributes={surveyAttributesQuery.data?.attributes ?? null}
                    onSettingChange={setSelectedSettingId}
                    isError={scoresByAttributeError.isError}
                    onRetry={scoresByAttributeError.retry}
                />
            </Flex>

            {/* The chart compares against the previous survey, so it doesn't apply at
                all without one — that's known upfront and isn't a loading state. */}
            {previousSurveyId && (
                <div className={pageBreakBefore}>
                    <DynamicAnalysisChart
                        scoresByCategory={currentSurveyScoresByCategory?.categories ?? null}
                        previousScoresByCategory={previousSurveyScoresByCategory?.categories ?? null}
                        isError={scoresByCategoryQuery.isError}
                        onRetry={scoresByCategoryQuery.refetch}
                    />
                </div>
            )}

            <div className={pageBreakBefore}>
                <ImprovementArea
                    scoresByCategory={currentSurveyScoresByCategory?.categories ?? null}
                    scoresByQuestion={currentSurveyScoresByQuestions?.questions ?? null}
                    isError={improvementAreaError.isError}
                    onRetry={improvementAreaError.retry}
                    renderQuestionLink={(questionId, pill) => (
                        <Link
                            to="/system/surveys/questions"
                            search={{ organizationId, surveyId: selectedSurveyId, questionId }}
                        >
                            {pill}
                        </Link>
                    )}
                />
            </div>
        </Flex>
    );
}

export function ContentSkeleton() {
    return (
        <Flex direction="column" gap="4">
            <Flex justify="end">
                <Skeleton.Box width={168} height={36} />
            </Flex>

            <Flex gap="4">
                <GlobalScoreSkeleton />
                <GlobalScoreSkeleton />
                <ResponderCountSkeleton />
            </Flex>

            <Flex gap="4">
                <TopCategorySkeleton />
                <TopCategorySkeleton />
            </Flex>

            <RankingsByDepartmentSkeleton />

            <Flex gap="4">
                <DiAverageChartPerYearSkeleton />
                <DiAverageChartPerAttributesSkeleton />
            </Flex>

            <DynamicAnalysisChartSkeleton />

            <ImprovementAreaSkeleton />
        </Flex>
    );
}
